import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useMemo } from 'react';
import type { ReactFlowInstance } from '@xyflow/react';
import { GraphNavDirection } from '../Cmarkdowngraphcontrols';
import type { MindmapFlowEdge, MindmapFlowNode, ParsedMindmap } from './mindmap-types';
import {
  getDirectionalMindmapNavigation,
  getMindmapKeyboardAction,
  getToggleActiveNodeNavigation,
  GRAPH_NAV_DIRECTION_TO_MINDMAP_KEY,
  MINDMAP_ZOOM_STEP_DURATION_MS,
  MindmapKeyboardActionKind,
  MindmapNavigationResultKind,
  type MindmapDirectionalKey,
  type MindmapExpansionInput,
  type MindmapNavigationResult,
  type SelectMindmapNodeInput,
} from './mindmap-navigation';

interface UseMindmapKeyboardNavigationInput {
  parsed: ParsedMindmap | null;
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge> | null;
  selectedNodeId: string | null;
  expandedNodeIds: Set<string>;
  parentMap: Map<string, string>;
  visibleNodeIds: Set<string>;
  visibleNodeYById: Map<string, number>;
  setSelectedMindmapNode: (input: SelectMindmapNodeInput) => void;
  updateNodeExpansion: (input: MindmapExpansionInput) => void;
}

interface UseMindmapKeyboardNavigationResult {
  handleKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  handleNavigate: (direction: GraphNavDirection) => void;
}

type KeyboardCommandRunner = () => void;
type NavigationResultApplier = (result: MindmapNavigationResult) => void;
type DirectionalKeyNavigationHandler = (key: MindmapDirectionalKey) => void;
type ActiveNodeToggleHandler = () => void;

export function useMindmapKeyboardNavigation(
  input: UseMindmapKeyboardNavigationInput,
): UseMindmapKeyboardNavigationResult {
  const {
    expandedNodeIds,
    parentMap,
    parsed,
    reactFlowInstance,
    selectedNodeId,
    setSelectedMindmapNode,
    updateNodeExpansion,
    visibleNodeIds,
    visibleNodeYById,
  } = input;
  const applyNavigationResult = useNavigationResultApplier({
    setSelectedMindmapNode,
    updateNodeExpansion,
  });
  const handleDirectionalKeyNavigation = useDirectionalKeyNavigation({
    applyNavigationResult,
    expandedNodeIds,
    parentMap,
    parsed,
    selectedNodeId,
    visibleNodeIds,
    visibleNodeYById,
  });
  const handleToggleActiveNode = useActiveNodeToggle({
    applyNavigationResult,
    parsed,
    selectedNodeId,
  });
  const keyboardCommandRunnerByKind = useKeyboardCommandRunnerByKind({
    handleToggleActiveNode,
    reactFlowInstance,
    setSelectedMindmapNode,
  });

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (!parsed) {
      return;
    }

    const keyboardAction = getMindmapKeyboardAction(event.key);
    if (keyboardAction.kind === MindmapKeyboardActionKind.None) {
      return;
    }

    event.preventDefault();
    if (keyboardAction.kind === MindmapKeyboardActionKind.Directional) {
      handleDirectionalKeyNavigation(keyboardAction.key);
      return;
    }

    keyboardCommandRunnerByKind[keyboardAction.kind]();
  }, [handleDirectionalKeyNavigation, keyboardCommandRunnerByKind, parsed]);

  const handleNavigate = useCallback((direction: GraphNavDirection): void => {
    handleDirectionalKeyNavigation(GRAPH_NAV_DIRECTION_TO_MINDMAP_KEY[direction]);
  }, [handleDirectionalKeyNavigation]);

  return {
    handleKeyDown,
    handleNavigate,
  };
}

function useNavigationResultApplier(input: {
  setSelectedMindmapNode: (input: SelectMindmapNodeInput) => void;
  updateNodeExpansion: (input: MindmapExpansionInput) => void;
}): NavigationResultApplier {
  const {
    setSelectedMindmapNode,
    updateNodeExpansion,
  } = input;

  return useCallback((result: MindmapNavigationResult): void => {
    if (result.kind === MindmapNavigationResultKind.SelectNode) {
      setSelectedMindmapNode({
        nodeId: result.nodeId,
        fit: result.fit,
      });
      return;
    }

    if (result.kind === MindmapNavigationResultKind.SetExpansion) {
      updateNodeExpansion({
        nodeId: result.nodeId,
        forceExpanded: result.forceExpanded,
      });
    }
  }, [setSelectedMindmapNode, updateNodeExpansion]);
}

function useDirectionalKeyNavigation(input: {
  applyNavigationResult: NavigationResultApplier;
  parsed: ParsedMindmap | null;
  selectedNodeId: string | null;
  expandedNodeIds: Set<string>;
  parentMap: Map<string, string>;
  visibleNodeIds: Set<string>;
  visibleNodeYById: Map<string, number>;
}): DirectionalKeyNavigationHandler {
  const {
    applyNavigationResult,
    expandedNodeIds,
    parentMap,
    parsed,
    selectedNodeId,
    visibleNodeIds,
    visibleNodeYById,
  } = input;

  return useCallback((key: MindmapDirectionalKey): void => {
    if (!parsed) {
      return;
    }

    applyNavigationResult(getDirectionalMindmapNavigation({
      key,
      root: parsed.root,
      selectedNodeId,
      expandedNodeIds,
      parentMap,
      visibleNodeIds,
      visibleNodeYById,
    }));
  }, [
    applyNavigationResult,
    expandedNodeIds,
    parentMap,
    parsed,
    selectedNodeId,
    visibleNodeIds,
    visibleNodeYById,
  ]);
}

function useActiveNodeToggle(input: {
  applyNavigationResult: NavigationResultApplier;
  parsed: ParsedMindmap | null;
  selectedNodeId: string | null;
}): ActiveNodeToggleHandler {
  const {
    applyNavigationResult,
    parsed,
    selectedNodeId,
  } = input;

  return useCallback((): void => {
    if (!parsed) {
      return;
    }

    applyNavigationResult(getToggleActiveNodeNavigation({
      root: parsed.root,
      selectedNodeId,
    }));
  }, [applyNavigationResult, parsed, selectedNodeId]);
}

function useKeyboardCommandRunnerByKind(input: {
  handleToggleActiveNode: ActiveNodeToggleHandler;
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge> | null;
  setSelectedMindmapNode: (input: SelectMindmapNodeInput) => void;
}): Record<MindmapKeyboardActionKind, KeyboardCommandRunner> {
  const {
    handleToggleActiveNode,
    reactFlowInstance,
    setSelectedMindmapNode,
  } = input;

  return useMemo<Record<
    MindmapKeyboardActionKind,
    KeyboardCommandRunner
  >>(() => {
    return {
      [MindmapKeyboardActionKind.None]: () => undefined,
      [MindmapKeyboardActionKind.ZoomIn]: () => {
        reactFlowInstance?.zoomIn({ duration: MINDMAP_ZOOM_STEP_DURATION_MS });
      },
      [MindmapKeyboardActionKind.ZoomOut]: () => {
        reactFlowInstance?.zoomOut({ duration: MINDMAP_ZOOM_STEP_DURATION_MS });
      },
      [MindmapKeyboardActionKind.ClearSelection]: () => {
        setSelectedMindmapNode({ nodeId: null, fit: false });
      },
      [MindmapKeyboardActionKind.Directional]: () => undefined,
      [MindmapKeyboardActionKind.ToggleActiveNode]: handleToggleActiveNode,
    };
  }, [
    handleToggleActiveNode,
    reactFlowInstance,
    setSelectedMindmapNode,
  ]);
}
